import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const serviceService = {
  // Get all active services
  async getAll() {
    const { data, error } = await supabase
      ?.from('services')
      ?.select('*')
      ?.eq('status', 'active')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Admin: Get all services (including inactive)
  async getAllForAdmin() {
    const { data, error } = await supabase
      ?.from('services')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Get service by ID with full details
  async getById(id) {
    const { data, error } = await supabase
      ?.from('services')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Get services by category
  async getByCategory(categoryId) {
    const { data, error } = await supabase
      ?.from('services')
      ?.select('*')
      ?.eq('category_id', categoryId)
      ?.eq('status', 'active');
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Get featured services
  async getFeatured() {
    const { data, error } = await supabase
      ?.from('services')
      ?.select('*')
      ?.eq('is_featured', true)
      ?.eq('status', 'active')
      ?.limit(6);
    if (error) {
      // Fallback: return any active services
      const { data: allData } = await supabase
        ?.from('services')
        ?.select('*')
        ?.eq('status', 'active')
        ?.limit(6);
      return toCamelCase(allData || []);
    }
    return toCamelCase(data || []);
  },

  // Create service (admin only)
  async create(serviceData) {
    // Map camelCase form fields to snake_case DB columns
    const snakeData = toSnakeCase({
      name: serviceData?.name,
      description: serviceData?.description,
      status: serviceData?.status || 'active',
      is_featured: serviceData?.isFeatured || false,
      avg_price: serviceData?.basePrice ? `₹${serviceData?.basePrice}` : null,
      price_min: serviceData?.basePrice ? parseFloat(serviceData?.basePrice) : null,
      slug: serviceData?.name
        ?.toLowerCase()
        ?.replace(/[^a-z0-9]+/g, '-')
        ?.replace(/(^-|-$)/g, '') + '-' + Date.now(),
    });
    const { data, error } = await supabase
      ?.from('services')
      ?.insert([snakeData])
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Update service (admin only)
  async update(id, serviceData) {
    const updatePayload = {
      name: serviceData?.name,
      description: serviceData?.description,
      status: serviceData?.status,
      is_featured: serviceData?.isFeatured,
      updated_at: new Date()?.toISOString(),
    };
    if (serviceData?.basePrice !== undefined) {
      updatePayload.avg_price = serviceData?.basePrice ? `₹${serviceData?.basePrice}` : null;
      updatePayload.price_min = serviceData?.basePrice ? parseFloat(serviceData?.basePrice) : null;
    }
    const { data, error } = await supabase
      ?.from('services')
      ?.update(updatePayload)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Delete service (admin only)
  async delete(id) {
    const { error } = await supabase
      ?.from('services')
      ?.delete()
      ?.eq('id', id);
    if (error) throw error;
  },

  // Search services
  async search(query) {
    const { data, error } = await supabase
      ?.from('services')
      ?.select('*')
      ?.eq('status', 'active')
      ?.ilike('name', `%${query}%`);
    if (error) return [];
    return toCamelCase(data || []);
  },
};